import React, { useState, useEffect, useMemo, memo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormContext, useWatch } from "react-hook-form";

import PropTypes from "prop-types";
import Paper from "@mui/material/Paper";

import SwiperTabs from "../../Swiper";
import Layout from "../Layouts/Layout";
import TabPanel from "./TabPanel";
import ActionButtons from "./ActionButtons";
import FormReview from "./FormReview";
import TabItems from "./TabItems";
import FormDialogs from "../Dialogs";
import PaymentTable from "../../../features/application/components/PaymentTable";
import Styled from "./styles";
import { methodRefs, requireFieldsInfo } from "../context/formSlice";
import { showNotification } from "../../../features/core/slices/notification.slice";
import useDatagridState from "../hooks/useDatagridState";
import KeyboardNavLink from "../../KeyboardNavLink";
import { showFormDialog } from "../context/dialogSlice";
import { togglePreSubmitDialog } from "../context/dialogFormSlice";
import {
  getDummyFields,
  getMissingTabBlockFields,
  getTabControllingKeys,
  getTabItems,
  mergeDgRecordsToMainForm,
} from "../services/form.wizard.service";
import {
  createDatagridMap,
  hasUnsavedDgRecord,
  verifyDgFields,
} from "../services/form.datagrid.service";
import { useErrorHandling } from "../../../ErrorBoundary";

function Wizard({
  formTemplate,
  showReviewTab,
  onSubmit,
  onError,
  formSaveHandler,
  printApplicationBtn,
}) {
  const dispatch = useDispatch();
  const { applicationId, applicationType } = useParams();
  const [index, setIndex] = useState(0);
  const applicationStatus = useSelector(
    (state) => state.formProps.applicationData?.status
  );
  const { formDialog } = useSelector((state) => state.formDialog);
  const initialFormData = useSelector((state) => state.formProps.formData);
  const dgDocuments = useSelector(
    (state) => state.formProps.formDocuments?.dgDocuments
  );
  const { domainName } = useSelector((state) => state.formProps.formConfig);
  const { getDgState, resetDgState, setDgKeysWithTabIndex } =
    useDatagridState();
  const {
    register,
    handleSubmit,
    getValues,
    control,
    setValue: formSetValue,
  } = useFormContext();
  const withErrorHandling = useErrorHandling();
  const isLicenseForm = domainName === "LicenseForm";
  const currentData = isLicenseForm ? initialFormData : getValues();

  // to track the disabled tabs and skip those tabs on clicking "save & next" btn
  const tabRef = useRef({});
  const tabIndexRef = useRef({});
  const swiperRef = useRef(null);
  const tabItems = useMemo(() => getTabItems(formTemplate), [formTemplate]);
  const tabControllingKeys = useMemo(
    () => getTabControllingKeys(tabItems),
    [tabItems]
  );

  const tabControllerValues = useWatch({
    control,
    name: tabControllingKeys,
    disabled: tabControllingKeys.length === 0,
  });

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      // reset the dgState, FormProps & MethodRefs
      resetDgState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // TODO: need to make it to work only for license update workflow
    let mounted = true;
    if (mounted && formTemplate && formTemplate.components.length > 0) {
      const dummyTextFields = getDummyFields(formTemplate.components);
      const registerAndSetValue = (key, _value) => {
        register(key);
        formSetValue(key, _value);
      };
      // register & set dummy field's initial values
      if (dummyTextFields.length)
        dummyTextFields.forEach((field) => {
          const val = initialFormData[field.key] ?? false;
          registerAndSetValue(field.key, val);
        });
      // register & set tab controlling field's initial value
      if (tabControllingKeys.length)
        tabControllingKeys.forEach((fieldKey) => {
          if (!dummyTextFields.includes(fieldKey))
            registerAndSetValue(fieldKey, initialFormData[fieldKey]);
        });
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormData, tabControllingKeys]);

  useEffect(() => {
    setDgKeysWithTabIndex(createDatagridMap(tabItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabItems]);

  const updateSwiperIndex = (nextIndex) => {
    if (swiperRef.current) swiperRef.current.slideTo(+nextIndex);
  };

  const allowTabChange = (tabIndex, type, next) => {
    if (!["Open", "Rejected"].includes(applicationStatus)) return true;
    const unsavedDgList = hasUnsavedDgRecord(
      getDgState,
      index,
      methodRefs,
      requireFieldsInfo
    );
    const isPureSave = type === "save" && !next;
    const missingFields = isPureSave
      ? []
      : getMissingTabBlockFields(
          getValues,
          tabItems[index]?.key,
          requireFieldsInfo
        );
    tabIndexRef.current = {
      tabIndex,
      type,
      next,
      unsavedDgList,
      missingFields,
    };
    if (!unsavedDgList.length && !missingFields.length) return true;
    if (missingFields.length) {
      const listItems = [
        {
          title: "Please address the missing information:",
          items: missingFields.map((f) => f.label),
        },
      ];
      dispatch(
        showNotification({
          type: "warning",
          config: { listItems },
          show: true,
        })
      );
    } else {
      dispatch(showFormDialog({ show: true, type: "dgWarning" }));
    }
    return false;
  };

  const handleTabChange = (event, newValue, skipDgCheck) => {
    if (skipDgCheck || allowTabChange(newValue, "tabChange")) {
      setIndex(newValue);
      updateSwiperIndex(newValue);
    }
  };

  const moveToNextValidTab = withErrorHandling((changeIndexTo, skipDgCheck) => {
    if (skipDgCheck || allowTabChange(changeIndexTo, "moveToNext")) {
      const direction = changeIndexTo > index ? 1 : -1;
      const tabs = Object.keys(tabRef.current);

      let foundValidTab = false;
      let nextIndex = index;
      while (!foundValidTab) {
        nextIndex += direction;

        if (direction === 1) {
          if (
            nextIndex >= changeIndexTo &&
            tabs.length > nextIndex &&
            tabRef.current[nextIndex] !== true
          ) {
            foundValidTab = true;
            break;
          }
          if (nextIndex >= tabs.length) {
            nextIndex = -1;
            break;
          }
        } else if (direction === -1) {
          if (
            nextIndex <= changeIndexTo &&
            tabs.length > nextIndex &&
            tabRef.current[nextIndex] !== true
          ) {
            foundValidTab = true;
            break;
          }
          if (nextIndex < 0) {
            nextIndex = -1;
            break;
          }
        }
      }
      if (nextIndex === -1) {
        if (applicationId === "new") {
          const validTabIndex = Object.values(tabRef.current).findIndex(
            (isDisable) => isDisable !== true
          );
          // Not able to stop the Swipe onDrag if value === validTabIndex
          setIndex(validTabIndex);
          updateSwiperIndex(validTabIndex);
        }
        if (showReviewTab && applicationId !== "new") {
          setIndex(tabItems.length);
          updateSwiperIndex(tabItems.length);
        }
      } else {
        setIndex(+nextIndex);
        updateSwiperIndex(+nextIndex);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  });

  const handleFormSave = async (next, dgRecordCheck, skipDgMerging = false) => {
    tabIndexRef.current = { type: "save", next };
    if (dgRecordCheck && !allowTabChange(null, "save", next)) return null;
    if (!skipDgMerging) {
      // to merge the current dg(if present) items to main form
      mergeDgRecordsToMainForm({
        methodRefs,
        getDgState,
        setValue: formSetValue,
        requireFieldsInfo,
      });
    }
    const result = await formSaveHandler();
    if (result) {
      if (next) moveToNextValidTab(index + 1, true);
      const msg = "Application Form Updated Successfully";
      dispatch(showNotification({ type: "success", msg, show: true }));
    }
    return result;
  };

  const handleDialogAction = () => {
    const { type, tabIndex, next } = tabIndexRef.current || {};
    if (type === "tabChange") {
      handleTabChange(null, tabIndex, true);
    } else if (type === "save") {
      handleFormSave(next, false);
    } else if (type === "moveToNext") {
      moveToNextValidTab(tabIndex, true);
    }
  };

  const validateAndSubmit = () => {
    const result = verifyDgFields(
      getDgState(),
      methodRefs,
      tabRef.current,
      requireFieldsInfo,
      dgDocuments,
      currentData
    );

    mergeDgRecordsToMainForm({
      methodRefs,
      getDgState,
      setValue: formSetValue,
      requireFieldsInfo,
    });

    if (result.isError) {
      const { errMsg, multiErrMsg } = result;
      const listItems = multiErrMsg
        .filter((err) => err.dgLabels.length)
        .map((err) => ({
          title: err.errMsg,
          items: err.dgLabels,
        }));
      if (errMsg)
        dispatch(
          showNotification({ type: "warning", msg: errMsg, show: true })
        );
      else if (listItems.length) {
        const config = { listItems };
        dispatch(showNotification({ type: "warning", config, show: true }));
      }
    } else if (requireFieldsInfo.preSubmitActions.payCycleSelection) {
      dispatch(
        togglePreSubmitDialog({ dialog: "payCycleSelection", open: true })
      );
    } else onSubmit(getValues());
  };

  const handleError = (error) => {
    onError(error);
  };

  const tabComponent = useMemo(
    () => (
      <TabItems
        value={index}
        tabRef={tabRef}
        tabItems={tabItems}
        currentData={currentData}
        showReviewTab={showReviewTab}
        applicationId={applicationId}
        handleTabChange={handleTabChange}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...tabControllerValues, index, currentData, applicationId]
  );

  const reviewTabItems = useMemo(
    () => (
      <FormReview
        value={index}
        tabItems={tabItems}
        currentData={currentData}
        handleError={handleError}
        handleSubmit={handleSubmit}
        applicationType={applicationType}
        validateAndSubmit={validateAndSubmit}
        applicationStatus={applicationStatus}
        handleTabChange={handleTabChange}
        formTemplate={formTemplate}
        printApplicationBtn={printApplicationBtn}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [index]
  );

  const buttonContainer = (
    <ActionButtons
      applicationId={applicationId}
      handleFormSave={handleFormSave}
      applicationStatus={applicationStatus}
    />
  );

  const tabPanels = tabItems.map((tab, i) => (
    <TabPanel
      value={index}
      index={i}
      // eslint-disable-next-line react/no-array-index-key
      key={`${tab.key}-${i}`}
    >
      <Layout components={tab.components} nested />
      {tab.properties?.showFees === "true" && <PaymentTable isDynamic />}
    </TabPanel>
  ));

  return (
    <Styled
      isReviewTab={showReviewTab && tabItems.length === index}
      tabIndex="0"
      id="wizard"
    >
      {tabComponent}
      <Paper className="paper" elevation={2}>
        {showReviewTab ? (
          <SwiperTabs
            swiperRef={swiperRef}
            onIndexChange={(nextIndex) => moveToNextValidTab(nextIndex)}
          >
            {tabPanels}
            {reviewTabItems}
          </SwiperTabs>
        ) : (
          <SwiperTabs
            swiperRef={swiperRef}
            onIndexChange={(nextIndex) => moveToNextValidTab(nextIndex)}
          >
            {tabPanels}
          </SwiperTabs>
        )}
        {showReviewTab && tabItems.length !== index && buttonContainer}
        <KeyboardNavLink
          type="button"
          addMargin={showReviewTab && tabItems.length !== index}
        />
      </Paper>
      {formDialog.show && (
        <FormDialogs
          handleFormSave={handleFormSave}
          handleDialogAction={handleDialogAction}
          tabIndexRef={tabIndexRef}
        />
      )}
    </Styled>
  );
}
Wizard.propTypes = {
  formTemplate: PropTypes.shape({
    components: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  showReviewTab: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  formSaveHandler: PropTypes.func,
};
Wizard.defaultProps = {
  formSaveHandler: undefined,
};
export default memo(Wizard);
